import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Icon } from "@/lib/icons";
import { Txt } from "@/components/Txt";
import { parseLocalDate } from "@/lib/dates";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const fmtDate = (s) => { const d = parseLocalDate(s); return d ? d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : ""; };

const brickTexture = {
  backgroundColor: "#8f3a2f",
  backgroundImage:
    "repeating-linear-gradient(0deg, rgba(0,0,0,0.18) 0 1px, transparent 1px 34px)," +
    "repeating-linear-gradient(90deg, rgba(0,0,0,0.18) 0 1px, transparent 1px 72px)," +
    "linear-gradient(135deg, #9c4034 0%, #7c2d22 100%)",
};

function EngravedPlaceholder({ inscription }) {
  const lines = (inscription || "").split("\n").filter(Boolean);
  return (
    <div data-testid="brick-placeholder" style={brickTexture}
      className="w-full aspect-[3/2] rounded-lg flex items-center justify-center p-5 shadow-inner border border-black/20">
      <div className="text-center text-cream/95 font-zilla leading-snug drop-shadow-[0_1px_0_rgba(0,0,0,0.45)]">
        {lines.length
          ? lines.map((l, i) => <div key={i} className="text-[18px] tracking-wide">{l}</div>)
          : <div className="text-[15px] italic text-cream/70">Rosemoor Legacy Brick</div>}
      </div>
    </div>
  );
}

function BrickCard({ b }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showPlaceholder = !b.photo_url || imgFailed;
  return (
    <div data-testid="my-brick-card"
      className="rounded-xl p-[3px] shadow-sm"
      style={{ backgroundImage: "repeating-linear-gradient(45deg, #b5543f 0 10px, #8f3a2f 10px 20px)" }}>
      <div className="bg-warm-cream rounded-[10px] overflow-hidden h-full flex flex-col">
        <div className="p-3 pb-0">
          {showPlaceholder
            ? <EngravedPlaceholder inscription={b.inscription} />
            : <img src={b.photo_url} alt="Engraved Rosemoor brick" onError={() => setImgFailed(true)}
                className="w-full aspect-[3/2] object-cover rounded-lg border border-ink/15" />}
        </div>
        <div className="p-4 flex flex-col gap-1.5">
          {/* Don't repeat the inscription — the placeholder already shows it. */}
          {!showPlaceholder && (b.inscription
            ? <div className="whitespace-pre-wrap font-zilla text-[19px] leading-snug text-ink">{b.inscription}</div>
            : <div className="font-zilla text-[19px] text-ink/50 italic">Rosemoor Legacy Brick</div>)}
          <div className="text-[13px] text-ink/70">
            {b.type_label}{b.type_label ? " · " : ""}You gave {money(b.amount_paid_cents)}
          </div>
          {b.purchase_date && <div className="text-[12px] text-ink/55">Purchased {fmtDate(b.purchase_date)}</div>}
        </div>
      </div>
    </div>
  );
}

export default function MyLegacyBricks() {
  const [bricks, setBricks] = useState([]);
  useEffect(() => { api.get("/bricks/mine").then((r) => setBricks(r.data.bricks || [])).catch(() => {}); }, []);
  if (bricks.length === 0) return null;

  const total = bricks.reduce((s, b) => s + (b.amount_paid_cents || 0), 0);
  const twoUp = bricks.length > 1;

  return (
    <div data-testid="my-legacy-bricks"
      className="rounded-2xl border border-maroon/25 bg-cream p-5 sm:p-6 shadow-sm"
      style={{ backgroundImage: "radial-gradient(circle at 100% 0%, rgba(128,0,0,0.06), transparent 45%)" }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon name="Blocks" className="w-5 h-5 text-maroon" />
        <h2 className="font-zilla text-2xl font-semibold text-ink">My Legacy Bricks</h2>
      </div>
      <p className="font-zilla text-[17px] text-maroon/90 leading-snug mb-2 max-w-xl" data-testid="bricks-thanks">
        <Txt id="bricks.member.thanks" screen="Member · Legacy Bricks">Thank you for investing in Rosemoor. Your brick is part of the corridor's story.</Txt>
      </p>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-maroon mb-4">Part of Rosemoor's story</div>

      <div className={`grid gap-4 ${twoUp ? "sm:grid-cols-2" : "grid-cols-1 max-w-md"}`} data-testid="my-bricks-grid">
        {bricks.map((b, i) => <BrickCard key={i} b={b} />)}
      </div>

      <div className="mt-4 pt-3 border-t border-ink/10 text-[13px] text-ink/70" data-testid="bricks-total">
        You've given <span className="font-semibold text-ink">{money(total)}</span> across {bricks.length} brick{bricks.length === 1 ? "" : "s"}.
      </div>

      <span data-testid="buy-brick-label"
        className="mt-4 inline-block border border-ink/15 text-ink/45 rounded-lg px-4 py-2 min-h-[40px] text-[13px] font-semibold select-none cursor-default">
        Coming soon
      </span>
    </div>
  );
}
